import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { FileText, Upload, Search, Trash2, ExternalLink, Loader2, CheckCircle2, AlertCircle, Clock, X, ArrowLeft, User, RefreshCw, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_CONFIG = {
  completed: { label: 'Completed', bg: 'bg-emerald-50', text: 'text-emerald-700', icon: CheckCircle2 },
  extracted: { label: 'Extracted', bg: 'bg-purple-50', text: 'text-purple-700', icon: Sparkles },
  processing: { label: 'Processing', bg: 'bg-amber-50', text: 'text-amber-600', icon: Loader2 },
  uploading: { label: 'Uploading', bg: 'bg-blue-50', text: 'text-blue-600', icon: Loader2 },
  failed: { label: 'Failed', bg: 'bg-red-50', text: 'text-red-600', icon: AlertCircle },
  unknown: { label: 'Unknown', bg: 'bg-slate-50', text: 'text-slate-500', icon: Clock },
};

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.unknown;
  const Icon = cfg.icon;
  const spin = status === 'processing' || status === 'uploading';
  return (
    <Badge className={`text-[10px] border-0 gap-1 ${cfg.bg} ${cfg.text}`} data-testid={`status-badge-${status}`}>
      <Icon className={`h-2.5 w-2.5 ${spin ? 'animate-spin' : ''}`} />{cfg.label}
    </Badge>
  );
};

const LuminaMyDocuments = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { authFetch } = useAuth();
  const patientId = searchParams.get('patientId');
  const fileRef = useRef(null);

  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [uploads, setUploads] = useState([]);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [patientName, setPatientName] = useState('');
  const [expandedRecord, setExpandedRecord] = useState(null);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [linkRecordId, setLinkRecordId] = useState(null);
  const [patientSearch, setPatientSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [linking, setLinking] = useState(false);
  const [extractingId, setExtractingId] = useState(null);
  const searchTimer = useRef(null);
  const pollTimers = useRef({});

  // Default to 'patient' mode if patientId present, else 'all'
  const [viewMode, setViewMode] = useState(() => {
    if (patientId) return 'patient';
    return localStorage.getItem('myDocs-viewMode') || 'all';
  });

  // Reset mode when patientId changes
  useEffect(() => {
    if (patientId) {
      setViewMode('patient');
    }
  }, [patientId]);

  // Persist mode selection
  useEffect(() => {
    localStorage.setItem('myDocs-viewMode', viewMode);
  }, [viewMode]);

  // Fetch patient name when patientId present
  useEffect(() => {
    if (!patientId) { setPatientName(''); return; }
    (async () => {
      try {
        const res = await authFetch(`${API}/api/patients/${patientId}`);
        if (res.ok) {
          const data = await res.json();
          const first = data.first_name || data.firstName || '';
          const last = data.last_name || data.lastName || '';
          setPatientName(first && last ? `${first} ${last}` : first || last || patientId);
        } else {
          setPatientName(patientId);
        }
      } catch {
        setPatientName(patientId);
      }
    })();
  }, [patientId, authFetch]);

  const fetchRecords = useCallback(async () => {
    setLoading(true);
    try {
      const isPatientMode = viewMode === 'patient' && patientId;
      const url = isPatientMode
        ? `${API}/api/patient-documents/patient/${patientId}/documents`
        : `${API}/api/patient-documents/history`;
      const res = await authFetch(url);
      if (!res.ok) throw new Error();
      const data = await res.json();
      setRecords(data.records || []);
    } catch {
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }, [viewMode, patientId, authFetch]);

  useEffect(() => { fetchRecords(); }, [fetchRecords]);
  useEffect(() => {
    // Capture the ref's current value so the cleanup uses the same set of
    // timers that the effect saw when it ran (the React lint guard).
    const timers = pollTimers.current;
    return () => { Object.values(timers).forEach(clearTimeout); };
  }, []);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';
    const entry = { id: `up-${Date.now()}`, filename: file.name, status: 'uploading', timestamp: new Date().toISOString(), analysis_id: null, source: 'my-documents' };
    setUploads(prev => [entry, ...prev]);
    try {
      const form = new FormData();
      form.append('file', file);
      // Pass patient_id when in patient mode
      const pidParam = (viewMode === 'patient' && patientId) ? `?patient_id=${encodeURIComponent(patientId)}` : '';
      const res = await authFetch(`${API}/api/patient-documents/upload${pidParam}`, { method: 'POST', body: form });
      if (!res.ok) throw new Error(`Upload failed (${res.status})`);
      const data = await res.json();
      const aid = data.analysis_id || data.id;
      setUploads(prev => prev.map(u => u.id === entry.id ? { ...u, status: 'processing', analysis_id: aid } : u));
      toast.success(`"${file.name}" uploaded — processing`);
      pollUpload(entry.id, aid);
    } catch (err) {
      setUploads(prev => prev.map(u => u.id === entry.id ? { ...u, status: 'failed' } : u));
      toast.error(err.message || 'Upload failed');
    }
  };

  const pollUpload = (entryId, analysisId) => {
    if (!analysisId) return;
    let attempts = 0;
    const poll = async () => {
      attempts++;
      try {
        const res = await authFetch(`${API}/api/analyses/${analysisId}`);
        if (res.ok) {
          const data = await res.json();
          const s = data.status?.toLowerCase();
          if (s === 'completed' || s === 'done') {
            setUploads(prev => prev.filter(u => u.id !== entryId));
            // Update local history record status + trigger extraction
            const matchRec = records.find(r => r.analysis_id === analysisId);
            if (matchRec?.id) {
              authFetch(`${API}/api/patient-documents/history/${matchRec.id}/status`, {
                method: 'PATCH', body: JSON.stringify({ status: 'completed' }),
              }).catch(() => {});
              // Auto-trigger AI extraction
              authFetch(`${API}/api/patient-documents/history/${matchRec.id}/extract`, { method: 'POST' })
                .then(() => fetchRecords())
                .catch(() => {});
            }
            fetchRecords();
            return;
          }
          if (s === 'failed' || s === 'error') {
            setUploads(prev => prev.filter(u => u.id !== entryId));
            const matchRec = records.find(r => r.analysis_id === analysisId);
            if (matchRec?.id) {
              authFetch(`${API}/api/patient-documents/history/${matchRec.id}/status`, {
                method: 'PATCH', body: JSON.stringify({ status: 'failed' }),
              }).catch(() => {});
            }
            fetchRecords();
            return;
          }
        }
      } catch { /* continue polling */ }
      if (attempts < 30) pollTimers.current[entryId] = setTimeout(poll, 3000);
      else {
        setUploads(prev => prev.filter(u => u.id !== entryId));
        fetchRecords();
      }
    };
    poll();
  };

  // Trigger extraction for any completed record that hasn't been extracted yet
  const triggerExtraction = async (recordId) => {
    setExtractingId(recordId);
    try {
      const pidParam = patientId ? `?patient_id=${encodeURIComponent(patientId)}` : '';
      const res = await authFetch(`${API}/api/patient-documents/history/${recordId}/extract${pidParam}`, { method: 'POST' });
      if (res.ok) {
        toast.success('Clinical data extracted');
        fetchRecords();
      } else {
        toast.error('Extraction failed');
      }
    } catch (err) {
      toast.error('Extraction failed');
    } finally {
      setExtractingId(null);
    }
  };

  // Link a document to a patient via modal search
  const openLinkDialog = (recordId) => {
    const rec = records.find(r => r.id === recordId);
    const extractedName = rec?.extracted_data?.patient_info?.name || '';
    setLinkRecordId(recordId);
    setPatientSearch(extractedName);
    setSearchResults([]);
    setLinkDialogOpen(true);
    // Auto-search if we have a name from extraction
    if (extractedName) {
      searchPatients(extractedName);
    }
  };

  const searchPatients = async (term) => {
    if (!term || term.length < 2) { setSearchResults([]); return; }
    setSearching(true);
    try {
      const res = await authFetch(`${API}/api/patients?search=${encodeURIComponent(term)}`);
      if (!res.ok) { setSearchResults([]); return; }
      const data = await res.json();
      setSearchResults(data.patients || data.data || []);
    } catch {
      setSearchResults([]);
    } finally {
      setSearching(false);
    }
  };

  const handlePatientSearchChange = (value) => {
    setPatientSearch(value);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => searchPatients(value), 400);
  };

  const confirmLinkPatient = async (patient) => {
    if (!linkRecordId) return;
    setLinking(true);
    try {
      const pid = patient.id || patient.patient_id;
      const res = await authFetch(`${API}/api/patient-documents/history/${linkRecordId}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ patient_id: pid }),
      });
      if (!res.ok) throw new Error('Link failed');
      const name = `${patient.first_name || ''} ${patient.last_name || ''}`.trim();
      toast.success(`Linked to ${name || pid}`);
      setLinkDialogOpen(false);
      setLinkRecordId(null);
      fetchRecords();
    } catch {
      toast.error('Failed to link document to patient');
    } finally {
      setLinking(false);
    }
  };

  const handleDelete = async (record) => {
    try {
      const res = await authFetch(`${API}/api/patient-documents/history/${record.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setRecords(prev => prev.filter(r => r.id !== record.id));
      toast.success('Record removed from history');
    } catch (err) {
      toast.error(err.message || 'Delete failed');
    }
    setDeleteConfirm(null);
  };

  const handleDeleteAnalysis = async (record) => {
    if (!record.analysis_id) return;
    try {
      const res = await authFetch(`${API}/api/analyses/${record.analysis_id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error(`Delete failed (${res.status})`);
      setRecords(prev => prev.filter(r => r.id !== record.id));
      toast.success('Analysis deleted');
    } catch (err) {
      toast.error(err.message || 'Delete analysis failed');
    }
  };

  const handleReprocess = async (record) => {
    if (!record.analysis_id) return;
    try {
      const res = await authFetch(`${API}/api/analyses/${record.analysis_id}/reprocess`, { method: 'POST' });
      if (!res.ok) throw new Error(`Reprocess failed (${res.status})`);
      setRecords(prev => prev.map(r => r.id === record.id ? { ...r, status: 'processing' } : r));
      toast.success('Re-processing started');
      pollUpload(`reprocess-${record.id}`, record.analysis_id);
    } catch (err) {
      toast.error(err.message || 'Reprocess failed');
    }
  };

  const allItems = [
    ...uploads.map(u => ({ ...u, _isUpload: true })),
    ...records.map(r => ({ ...r, _isUpload: false })),
  ];

  const filtered = allItems.filter(item => {
    const name = (item.filename || item.file_name || '').toLowerCase();
    if (searchQuery && !name.includes(searchQuery.toLowerCase())) return false;
    if (statusFilter !== 'all') {
      const s = (item.status || '').toLowerCase();
      if (statusFilter === 'completed' && s !== 'completed' && s !== 'done' && s !== 'extracted') return false;
      if (statusFilter === 'processing' && s !== 'processing' && s !== 'uploading') return false;
      if (statusFilter === 'failed' && s !== 'failed' && s !== 'error') return false;
    }
    return true;
  });

  const isPatientMode = viewMode === 'patient' && patientId;
  const hasNoPatientDocs = isPatientMode && !loading && filtered.length === 0 && uploads.length === 0;

  return (
    <div className="max-w-5xl mx-auto py-8 px-6" data-testid="my-documents-page">
      {/* Patient context banner */}
      {patientId && (
        <div className="flex items-center gap-3 mb-4" data-testid="patient-context-bar">
          <button
            onClick={() => navigate(`/lumina/clinical-focus?patientId=${patientId}`)}
            className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium text-slate-500 hover:text-slate-800 hover:bg-slate-100 transition-colors"
            style={{ border: '0.5px solid #e2e2e2', background: '#fff', cursor: 'pointer' }}
            data-testid="back-to-chart-btn"
          >
            <ArrowLeft className="h-3 w-3" />Back to Patient Chart
          </button>
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-blue-50 border border-blue-100" data-testid="patient-context-chip">
            <User className="h-3 w-3 text-blue-500" />
            <span className="text-[11px] font-medium text-blue-700">
              {patientName || patientId}
            </span>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 font-['Plus_Jakarta_Sans']">My Documents</h1>
          <p className="text-sm text-slate-500 mt-1">Upload, view, and manage your documents</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/lumina/settings?tab=documents')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors"
            style={{ border: '0.5px solid #e0e0e0', background: '#fff', cursor: 'pointer' }}
            data-testid="open-settings-documents"
          >
            <ExternalLink className="h-3 w-3" />Open in Settings
          </button>
          <button
            onClick={() => fileRef.current?.click()}
            className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium bg-slate-900 text-white hover:bg-slate-800 transition-colors"
            style={{ border: 'none', cursor: 'pointer' }}
            data-testid="my-docs-upload-btn"
          >
            <Upload className="h-3.5 w-3.5" />Upload Document
          </button>
          <input ref={fileRef} type="file" accept=".pdf,.png,.jpg,.jpeg,.xml,.json" className="hidden" onChange={handleUpload} data-testid="my-docs-upload-input" />
        </div>
      </div>

      {/* Mode toggle + Filters */}
      <div className="flex items-center gap-3 mb-4">
        {/* Mode toggle — only show when patientId is present */}
        {patientId && (
          <div className="flex items-center gap-0.5 bg-slate-100 rounded-lg p-0.5 mr-1" data-testid="view-mode-toggle">
            <button
              onClick={() => setViewMode('patient')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === 'patient' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              style={{ border: 'none', cursor: 'pointer' }}
              data-testid="mode-current-patient"
            >
              Current Patient
            </button>
            <button
              onClick={() => setViewMode('all')}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${viewMode === 'all' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              style={{ border: 'none', cursor: 'pointer' }}
              data-testid="mode-all-documents"
            >
              All Documents
            </button>
          </div>
        )}

        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
          <input
            type="text"
            placeholder="Search by filename..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400 transition-colors"
            data-testid="my-docs-search"
          />
        </div>
        <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
          {['all', 'completed', 'processing', 'failed'].map(f => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${statusFilter === f ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              style={{ border: 'none', cursor: 'pointer' }}
              data-testid={`filter-${f}`}
            >
              {f === 'all' ? 'All' : f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
        <span className="text-xs text-slate-400 ml-auto">{filtered.length} document{filtered.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden" data-testid="my-docs-table">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/50">
              <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Filename</th>
              <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Source</th>
              <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Status</th>
              <th className="text-left px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Uploaded</th>
              <th className="text-right px-4 py-2.5 text-[10px] font-semibold uppercase tracking-wide text-slate-400">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin mx-auto mb-2 text-slate-300" />Loading...
              </td></tr>
            )}
            {/* Patient-specific empty state */}
            {hasNoPatientDocs && (
              <tr><td colSpan={5} className="px-4 py-12 text-center" data-testid="patient-empty-state">
                <FileText className="h-8 w-8 text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No documents for this patient</p>
                <p className="text-xs text-slate-400 mt-1 mb-3">Upload a document or switch to view all documents</p>
                <button
                  onClick={() => setViewMode('all')}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium text-blue-600 hover:bg-blue-50 transition-colors"
                  style={{ border: '0.5px solid #bfdbfe', background: '#fff', cursor: 'pointer' }}
                  data-testid="view-all-docs-btn"
                >
                  View all documents
                </button>
              </td></tr>
            )}
            {/* Generic empty state (no patient context) */}
            {!loading && filtered.length === 0 && !hasNoPatientDocs && (
              <tr><td colSpan={5} className="px-4 py-12 text-center">
                <FileText className="h-8 w-8 text-slate-200 mx-auto mb-3" />
                <p className="text-sm text-slate-500">No documents yet</p>
                <p className="text-xs text-slate-400 mt-1">Upload a document to get started</p>
              </td></tr>
            )}
            {!loading && filtered.map((item, i) => {
              const fname = item.filename || item.file_name || 'Untitled';
              const status = (item.status || 'unknown').toLowerCase();
              const source = item.source || item.doc_type || 'Upload';
              const time = item.timestamp ? new Date(item.timestamp).toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';
              const canDelete = !item._isUpload && item.id;
              return (
                <React.Fragment key={item.id || i}>
                <tr className="border-b border-slate-50 hover:bg-slate-50/50 transition-colors" data-testid={`doc-row-${item.id || i}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <FileText className="h-3.5 w-3.5 text-slate-400 shrink-0" />
                      <span className="text-sm font-medium text-slate-800 truncate max-w-[280px]">{fname}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3"><span className="text-xs text-slate-500 capitalize">{source}</span></td>
                  <td className="px-4 py-3"><StatusBadge status={status === 'done' ? 'completed' : status === 'error' ? 'failed' : status} /></td>
                  <td className="px-4 py-3"><span className="text-xs text-slate-400">{time}</span></td>
                  <td className="px-4 py-3 text-right">
                    <div className="flex items-center justify-end gap-1">
                      {/* Extract — show for completed items that haven't been extracted yet */}
                      {!item._isUpload && (status === 'completed' || status === 'done') && !item.extracted_data && (
                        <button
                          onClick={() => triggerExtraction(item.id)}
                          disabled={extractingId === item.id}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium text-purple-600 hover:bg-purple-50 transition-colors disabled:opacity-50"
                          style={{ background: 'none', border: 'none', cursor: extractingId === item.id ? 'wait' : 'pointer' }}
                          data-testid={`extract-doc-${item.id}`}
                        >
                          {extractingId === item.id ? (
                            <><Loader2 className="h-3 w-3 animate-spin" />Extracting...</>
                          ) : (
                            <><Sparkles className="h-3 w-3" />Extract Data</>
                          )}
                        </button>
                      )}
                      {/* Show extracting state for the record even after status changes */}
                      {extractingId === item.id && item.extracted_data && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium text-purple-600">
                          <Loader2 className="h-3 w-3 animate-spin" />Extracting...
                        </span>
                      )}
                      {/* Link to patient — show for extracted items without patient_id */}
                      {item.extracted_data && !item.patient_id && (
                        <button
                          onClick={() => openLinkDialog(item.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium text-blue-600 hover:bg-blue-50 transition-colors"
                          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                          data-testid={`link-patient-${item.id}`}
                        >
                          <User className="h-3 w-3" />Link Patient
                        </button>
                      )}
                      {/* View extracted data */}
                      {item.extracted_data && (
                        <button
                          onClick={() => setExpandedRecord(expandedRecord === item.id ? null : item.id)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium text-emerald-600 hover:bg-emerald-50 transition-colors"
                          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                          data-testid={`view-extracted-${item.id}`}
                        >
                          <CheckCircle2 className="h-3 w-3" />View Data
                        </button>
                      )}
                      {/* Reprocess — show for failed/completed items with analysis_id */}
                      {!item._isUpload && item.analysis_id && (status === 'failed' || status === 'error') && (
                        <button
                          onClick={() => handleReprocess(item)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium text-blue-600 hover:bg-blue-50 transition-colors"
                          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                          data-testid={`reprocess-doc-${item.id}`}
                        >
                          <RefreshCw className="h-3 w-3" />Retry
                        </button>
                      )}
                      {/* Delete analysis — for completed items with analysis_id */}
                      {!item._isUpload && item.analysis_id && (status === 'completed' || status === 'done') && (
                        <button
                          onClick={() => handleDeleteAnalysis(item)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium text-amber-600 hover:bg-amber-50 transition-colors"
                          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                          data-testid={`delete-analysis-${item.id}`}
                        >
                          <Trash2 className="h-3 w-3" />Analysis
                        </button>
                      )}
                      {/* Remove from history */}
                      {canDelete ? (
                        <button
                          onClick={() => setDeleteConfirm(item)}
                          className="inline-flex items-center gap-1 px-2 py-1 rounded text-[10px] font-medium text-red-600 hover:bg-red-50 transition-colors"
                          style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                          data-testid={`delete-doc-${item.id}`}
                        >
                          <Trash2 className="h-3 w-3" />Remove
                        </button>
                      ) : !item._isUpload ? (
                        <span className="text-[10px] text-slate-300" title="Manage in Settings">—</span>
                      ) : null}
                    </div>
                  </td>
                </tr>
                {/* Extracted data expandable row */}
                {expandedRecord === item.id && item.extracted_data && (
                  <tr key={`expanded-${item.id}`}>
                    <td colSpan={5} className="px-4 py-3 bg-slate-50/80">
                      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 text-xs">
                        {item.extracted_data.summary && (
                          <div className="col-span-full p-2 rounded bg-white border border-slate-100">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Summary</p>
                            <p className="text-slate-700">{item.extracted_data.summary}</p>
                          </div>
                        )}
                        {(item.extracted_data.problems || []).length > 0 && (
                          <div className="p-2 rounded bg-white border border-slate-100">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Problems ({item.extracted_data.problems.length})</p>
                            {item.extracted_data.problems.map((p, pi) => (
                              <p key={pi} className="text-slate-700">{p.name || p}</p>
                            ))}
                          </div>
                        )}
                        {(item.extracted_data.medications || []).length > 0 && (
                          <div className="p-2 rounded bg-white border border-slate-100">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Medications ({item.extracted_data.medications.length})</p>
                            {item.extracted_data.medications.map((m, mi) => (
                              <p key={mi} className="text-slate-700">{m.name || m}{m.dose ? ` ${m.dose}` : ''}</p>
                            ))}
                          </div>
                        )}
                        {(item.extracted_data.allergies || []).length > 0 && (
                          <div className="p-2 rounded bg-white border border-slate-100">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Allergies ({item.extracted_data.allergies.length})</p>
                            {item.extracted_data.allergies.map((a, ai) => (
                              <p key={ai} className="text-slate-700">{a.substance || a}</p>
                            ))}
                          </div>
                        )}
                        {(item.extracted_data.vitals || []).length > 0 && (
                          <div className="p-2 rounded bg-white border border-slate-100">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Vitals ({item.extracted_data.vitals.length})</p>
                            {item.extracted_data.vitals.map((v, vi) => (
                              <p key={vi} className="text-slate-700">{v.name}: {v.value} {v.unit || ''}</p>
                            ))}
                          </div>
                        )}
                        {item.extracted_data.assessment_plan && (
                          <div className="col-span-full p-2 rounded bg-white border border-slate-100">
                            <p className="text-[10px] font-semibold text-slate-400 uppercase mb-1">Assessment & Plan</p>
                            <p className="text-slate-700">{item.extracted_data.assessment_plan}</p>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Delete confirmation */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" data-testid="delete-doc-confirm-dialog">
          <div className="bg-white rounded-xl shadow-xl p-5 w-full max-w-sm mx-4" style={{ animation: 'fadeIn 0.15s ease' }}>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-slate-900">Remove document?</h3>
              <button onClick={() => setDeleteConfirm(null)} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X className="h-4 w-4 text-slate-400" /></button>
            </div>
            <p className="text-xs text-slate-500 mb-4">
              This removes <strong>{deleteConfirm.filename || deleteConfirm.file_name}</strong> from your history. This action cannot be undone.
            </p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setDeleteConfirm(null)} className="px-3 py-1.5 rounded-lg text-xs font-medium text-slate-600 hover:bg-slate-100 transition-colors" style={{ border: '0.5px solid #e0e0e0', background: '#fff', cursor: 'pointer' }} data-testid="cancel-delete-doc">Cancel</button>
              <button onClick={() => handleDelete(deleteConfirm)} className="px-3 py-1.5 rounded-lg text-xs font-medium text-white bg-red-600 hover:bg-red-700 transition-colors" style={{ border: 'none', cursor: 'pointer' }} data-testid="confirm-delete-doc">Remove</button>
            </div>
          </div>
          <style>{`@keyframes fadeIn { from { opacity:0; transform:scale(0.96); } to { opacity:1; transform:scale(1); } }`}</style>
        </div>
      )}

      {/* Link to Patient Dialog */}
      <Dialog open={linkDialogOpen} onOpenChange={(open) => { if (!open) { setLinkDialogOpen(false); setLinkRecordId(null); } }}>
        <DialogContent className="sm:max-w-md" data-testid="link-patient-dialog">
          <DialogHeader>
            <DialogTitle className="text-base font-semibold">Link Document to Patient</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Search for a patient by name or MRN to link this document.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
              <input
                type="text"
                placeholder="Search by patient name or MRN..."
                value={patientSearch}
                onChange={(e) => handlePatientSearchChange(e.target.value)}
                className="w-full pl-9 pr-3 py-2.5 rounded-lg border border-slate-200 text-sm outline-none focus:border-slate-400 focus:ring-1 focus:ring-slate-200 transition-colors"
                autoFocus
                data-testid="link-patient-search-input"
              />
              {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400 animate-spin" />}
            </div>
            <div className="max-h-60 overflow-y-auto rounded-lg border border-slate-100" data-testid="link-patient-results">
              {!searching && searchResults.length === 0 && patientSearch.length >= 2 && (
                <div className="px-4 py-6 text-center">
                  <User className="h-5 w-5 text-slate-300 mx-auto mb-2" />
                  <p className="text-xs text-slate-400">No patients found</p>
                </div>
              )}
              {!searching && patientSearch.length < 2 && (
                <div className="px-4 py-6 text-center">
                  <p className="text-xs text-slate-400">Type at least 2 characters to search</p>
                </div>
              )}
              {searchResults.map((p, idx) => {
                const name = `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown';
                const pid = p.id || p.patient_id || '';
                const dob = p.date_of_birth || p.dob || '';
                return (
                  <button
                    key={pid || idx}
                    onClick={() => confirmLinkPatient(p)}
                    disabled={linking}
                    className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-b-0 disabled:opacity-50"
                    style={{ background: 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid #f8fafc' }}
                    data-testid={`link-patient-option-${idx}`}
                  >
                    <div className="flex-shrink-0 h-8 w-8 rounded-full bg-blue-50 flex items-center justify-center">
                      <User className="h-3.5 w-3.5 text-blue-500" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-800 truncate">{name}</p>
                      <p className="text-[10px] text-slate-400">
                        {pid && <span>ID: {pid}</span>}
                        {dob && <span className="ml-2">DOB: {dob}</span>}
                      </p>
                    </div>
                    {linking && <Loader2 className="h-3.5 w-3.5 text-slate-400 animate-spin flex-shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LuminaMyDocuments;
